import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditMove } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import { extractLesson } from "cc-frontend/lib/transferrable-date-utils"
import {
  consolidateCourseDatesOff,
  constructUnitsAndDates,
  insertRange,
} from "cc-frontend/lib/unit-utils"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { partial } from "lodash-es"
import moment from "moment"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultLessonModels from "../../lesson/default-lesson-models"

interface Payload {
  cardStackId: string
  fromDate: string
  toDate: string
  course: Course
  planbook: Planbook
  rotationCalendar: RotationCalendar
}

type MovingCalendarPatch = PatchModel & {
  cardStackIdsOnTheMove?: string[]
}

export function prepareMoveLessonToDate(payload: Payload): PreparedAction {
  return {
    name: MOVE_LESSON_TO_DATE.name,
    payload: payload,
    patches: MOVE_LESSON_TO_DATE.patches(payload),
    undoPatches: MOVE_LESSON_TO_DATE.undoPatches(payload),
    narrative: partial(MOVE_LESSON_TO_DATE.narrative, payload),
  }
}

export const MOVE_LESSON_TO_DATE = {
  name: "MOVE_LESSON_TO_DATE",

  params: {
    cardStackId: "string",
    fromDate: "string",
    toDate: "number",
    course: "object",
    planbook: "object",
    rotationCalendar: "object",
  },

  patches(payload: Payload): Array<PatchModel> {
    let schoolDays = payload.rotationCalendar.attributes.schoolDays

    let consolidatedCourseDatesOff = consolidateCourseDatesOff(
      payload.course,
      payload.planbook,
      payload.rotationCalendar
    )

    let { transferrableArray, courseDatesWithoutLesson } = extractLesson(
      payload.cardStackId,
      payload.course.attributes.calendar.dates
    )

    let newCourseDates = insertRange(
      transferrableArray,
      payload.toDate,
      0,
      null,
      courseDatesWithoutLesson,
      consolidatedCourseDatesOff,
      schoolDays,
      true
    )
    let { courseDates, units } = constructUnitsAndDates(
      newCourseDates,
      consolidatedCourseDatesOff,
      schoolDays
    )

    let patch: MovingCalendarPatch = Patch.create("course", payload.course.id)
      .set(`attributes.calendar.dates`, courseDates)
      .set(`attributes.calendar.units`, units)
      .inc(`attributes.calendar.version`, 1).patch

    let updateLessonPatch = Patch.create("card-stack", payload.cardStackId)
      .set("relationships.course.data.id", payload.course.id)
      .set("relationships.planbook.data.id", payload.planbook.id)
      .set("attributes.schemaVersion", payload.planbook.attributes.flags.unifiedLessons ? 2 : 1)
      .set("attributes.usedAs", "lesson")
      .set("meta.permissions", payload.course.meta.permissions).patch // to make sure we have the right permissions if this is new

    // patch.unitIdsOnTheMove   = unitIdsOnTheMove
    // @ts-ignore
    patch.cardStackIdsOnTheMove = [payload.cardStackId]

    return [patch, updateLessonPatch]
  },

  undoPatches(payload: Payload): Array<PatchModel> {
    let patch = Patch.create("course", payload.course.id)
      .set(
        "attributes.calendar.dates",
        JSON.parse(JSON.stringify(payload.course.attributes.calendar.dates))
      )
      .set(
        "attributes.calendar.units",
        JSON.parse(JSON.stringify(payload.course.attributes.calendar.units))
      )
      .inc("attributes.calendar.version", 1).patch

    return [patch]
  },

  narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
    let course = findFn("course", payload.course.id)
    let models = defaultLessonModels(payload, findFn, userId)

    let courseId = models.course?.id
    // The payload always carries the planbook; `models.planbook` is a store lookup that can miss.
    let planbookId = payload.planbook.id
    let lessonTitle = models.cardStack?.attributes.title || "Blank Lesson"
    let formattedToDate = moment(payload.toDate).format("ddd, MMM. D, YYYY")
    let newEvent: LessonEditMove = {
      ...createBlankEvent(),
      root_id: payload.planbook.id,
      root_type: "planbook",
      date_plan_scheduled_for: models.dateStringForCardStack,
      primary_object_id: payload.cardStackId,
      primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
      primary_verb: "move",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbook.id),
      primary_object_type: "lesson",
      primary_object_specifier: null,

      adverbial_verb: "dragging_dropping",
      adverbial_object_type: "lesson",
      adverbial_object_specifier: null,
      adverbial_object_id: payload.cardStackId,
      adverbial_possessive: null,
      adverbial_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    }

    return {
      title: "Moved Lesson",
      titleForAnalytics: "Lesson - Move",
      description: `Moved "${lessonTitle}" to ${formattedToDate}`,
      context: {
        lessonTitle: lessonTitle,
        triggeredBy: "dragging",
        courseTitle: models?.course?.attributes.title,
        planbookTitle: payload.planbook.attributes.title,
        cardStackId: payload.cardStackId,
        courseId: payload.course.id,
        planbookId: payload.planbook.id,
      },
      activity: {
        actor: `user-${userId}`,
        verb: "edited",
        object: `lesson-${payload.cardStackId}`,
        to: [`course-edits:course-${courseId}`, `planbook-edits:planbook-${planbookId}`],
      },
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default MOVE_LESSON_TO_DATE
